import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from '../i18n'
import { useAuth, authFetch, API_BASE_URL, getStoredToken } from '../utils/auth'
import SEO from '../components/SEO'
import Markdown from 'react-markdown'
import './DashboardPage.css'

// ── Types ──
interface ProfileData {
  id?: string | null
  user_id: string
  name: string
  is_active: boolean
  position?: string | null
  company_name?: string | null
  job_description?: string | null
  cv_text?: string | null
  cv_summary?: string | null
  jd_summary?: string | null
  created_at?: string | null
  updated_at?: string | null
}

function DashboardPage() {
  const { t } = useTranslation()
  const { user, loading, logout, refreshUser } = useAuth()
  const navigate = useNavigate()

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { state: { from: '/dashboard' }, replace: true })
    }
  }, [loading, user, navigate])

  // Refresh user data on mount (picks up subscription changes)
  useEffect(() => {
    if (user) {
      refreshUser()
    }
    // Only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Interview Profile State ──
  const [profiles, setProfiles] = useState<ProfileData[]>([])
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [creating, setCreating] = useState(false)
  const [profileForm, setProfileForm] = useState({
    name: '',
    position: '',
    company_name: '',
    job_description: '',
    cv_text: '',
  })
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileStatus, setProfileStatus] = useState<'idle' | 'saved' | 'error' | 'uploaded'>('idle')
  const [uploading, setUploading] = useState<'cv' | 'jd' | null>(null)
  const cvFileRef = useRef<HTMLInputElement>(null)
  const jdFileRef = useRef<HTMLInputElement>(null)

  // Load all profiles on mount
  const loadProfiles = useCallback(async () => {
    if (!user) return
    try {
      const data = await authFetch<{
        profiles: ProfileData[]
        active_profile_id?: string | null
      }>('/profile/interviews')
      setProfiles(data.profiles || [])
      setActiveProfileId(data.active_profile_id || null)
    } catch {
      // No profiles yet
      setProfiles([])
    }
  }, [user])

  useEffect(() => {
    loadProfiles()
  }, [loadProfiles])

  // Currently selected (viewed) profile
  const activeProfile = profiles.find((p) => p.id === activeProfileId) || profiles[0] || null

  // ── Handlers ──

  const handleProfileChange = useCallback(
    (field: string, value: string) => {
      setProfileForm((prev) => ({ ...prev, [field]: value }))
      setProfileStatus('idle')
    },
    []
  )

  const startCreating = () => {
    setCreating(true)
    setEditMode(true)
    setProfileForm({ name: '', position: '', company_name: '', job_description: '', cv_text: '' })
    setProfileStatus('idle')
  }

  const startEditing = () => {
    if (!activeProfile) return
    setCreating(false)
    setEditMode(true)
    setProfileForm({
      name: activeProfile.name || '',
      position: activeProfile.position || '',
      company_name: activeProfile.company_name || '',
      job_description: activeProfile.job_description || '',
      cv_text: activeProfile.cv_text || '',
    })
    setProfileStatus('idle')
  }

  const cancelEditing = () => {
    setEditMode(false)
    setCreating(false)
    setProfileStatus('idle')
  }

  const handleProfileSave = async () => {
    setProfileSaving(true)
    setProfileStatus('idle')
    try {
      if (creating) {
        await authFetch('/profile/interview', {
          method: 'POST',
          body: JSON.stringify(profileForm),
        })
      } else if (activeProfile?.id) {
        await authFetch(`/profile/interview/${activeProfile.id}`, {
          method: 'PUT',
          body: JSON.stringify(profileForm),
        })
      } else {
        await authFetch('/profile/interview', {
          method: 'PUT',
          body: JSON.stringify(profileForm),
        })
      }
      setProfileStatus('saved')
      setEditMode(false)
      setCreating(false)
      await loadProfiles()
    } catch {
      setProfileStatus('error')
    } finally {
      setProfileSaving(false)
    }
  }

  const handleSwitchProfile = async (profileId: string) => {
    try {
      await authFetch('/profile/interview/activate', {
        method: 'POST',
        body: JSON.stringify({ profile_id: profileId }),
      })
      setActiveProfileId(profileId)
      setEditMode(false)
      await loadProfiles()
    } catch { /* ignore */ }
  }

  const handleDeleteProfile = async (profileId: string) => {
    try {
      await authFetch(`/profile/interview/${profileId}`, { method: 'DELETE' })
      setEditMode(false)
      setCreating(false)
      setProfileStatus('idle')
      await loadProfiles()
    } catch { /* ignore */ }
  }

  const handleFileUpload = async (field: 'cv' | 'jd', file: File) => {
    setUploading(field)
    try {
      const formData = new FormData()
      formData.append('file', file)

      const token = getStoredToken()
      const response = await fetch(`${API_BASE_URL}/profile/interview/extract`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.detail || 'Upload failed')
      }

      const { text } = await response.json() as { text: string; filename: string }

      // Put extracted text into the form field (does NOT save to DB)
      if (field === 'cv') {
        setProfileForm((prev) => ({ ...prev, cv_text: text }))
      } else {
        setProfileForm((prev) => ({ ...prev, job_description: text }))
      }

      setProfileStatus('uploaded')
    } catch {
      setProfileStatus('error')
    } finally {
      setUploading(null)
    }
  }

  if (loading || !user) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-loading">{t('dashboardLoading')}</div>
      </div>
    )
  }

  const trialPercent = user.trial_seconds_limit > 0
    ? Math.round((user.trial_seconds_used / user.trial_seconds_limit) * 100)
    : 0

  const remainingMinutes = Math.floor(user.trial_seconds_remaining / 60)
  const remainingSecs = user.trial_seconds_remaining % 60

  const isTrialActive = user.subscription_status === 'trialing' && user.trial_seconds_remaining > 0
  const isExpired = !user.is_paid && !isTrialActive
  const isPro = user.is_paid
  const planLabel = user.plan === 'pro' ? t('dashboardBadgePro') : user.plan === 'basic' ? t('dashboardBadgeBasic') : null

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <div className="dashboard-page">
      <SEO
        title="Dashboard"
        description="Your Intervio dashboard. Manage your subscription and download the desktop app."
        path="/dashboard"
        noIndex
      />
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1>{t('dashboardTitle')}</h1>
            <p className="dashboard-email">{user.email}</p>
          </div>
          <button className="btn btn-secondary" onClick={handleLogout}>
            {t('dashboardLogout')}
          </button>
        </div>

        {/* Subscription Card */}
        <div className={`sub-card ${isPro ? 'sub-pro' : isExpired ? 'sub-expired' : 'sub-trial'}`}>
          <div className="sub-card-header">
            <h2>{t('dashboardSubscription')}</h2>
            <span className={`sub-badge ${isPro ? 'badge-pro' : isExpired ? 'badge-expired' : 'badge-trial'}`}>
              {isPro ? (planLabel || t('dashboardBadgePro')) : isExpired ? t('dashboardBadgeExpired') : t('dashboardBadgeTrial')}
            </span>
          </div>

          {!isPro && (
            <div className="trial-progress">
              <div className="trial-bar">
                <div
                  className="trial-bar-fill"
                  style={{ width: `${Math.min(trialPercent, 100)}%` }}
                />
              </div>
              <div className="trial-stats">
                <span>{t('dashboardUsed')}: {Math.floor(user.trial_seconds_used / 60)}m</span>
                <span>
                  {t('dashboardRemaining')}: {remainingMinutes}m {remainingSecs}s
                </span>
              </div>
            </div>
          )}

          {isPro && (
            <p className="sub-active-msg">
              {user.plan === 'basic' ? t('dashboardBasicActive') : t('dashboardProActive')}
            </p>
          )}

          {isExpired && (
            <div className="sub-expired-cta">
              <p>{t('dashboardTrialExpired')}</p>
              <Link to="/pricing" className="btn btn-primary">
                {t('dashboardUpgrade')}
              </Link>
            </div>
          )}

          {isTrialActive && !isPro && (
            <Link to="/pricing" className="btn btn-secondary sub-upgrade-link">
              {t('dashboardUpgrade')}
            </Link>
          )}
        </div>

        {/* Download Desktop CTA */}
        <div className="download-card">
          <div className="download-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <path d="M8 21h8M12 17v4" />
            </svg>
          </div>
          <div className="download-content">
            <h3>{t('dashboardDownloadTitle')}</h3>
            <p>{t('dashboardDownloadDesc')}</p>
          </div>
          <div className="download-info">
            <Link to="/download" className="btn btn-primary">
              {t('navDownload')}
            </Link>
            <p className="download-note">{t('dashboardDownloadNote')}</p>
          </div>
        </div>

        {/* ── Interview Profiles Section ── */}
        <div className="profile-section">
          <div className="profile-section-header">
            <div>
              <h2>{t('profileTitle')}</h2>
              <p className="profile-card-desc">{t('profileDesc')}</p>
            </div>
            <button className="btn btn-primary btn-sm" onClick={startCreating}>
              + {t('profileNewProfile')}
            </button>
          </div>

          {/* Profile Tabs / Switcher */}
          {profiles.length > 0 && (
            <div className="profile-tabs">
              {profiles.map((p) => (
                <button
                  key={p.id}
                  className={`profile-tab ${p.id === activeProfileId ? 'profile-tab-active' : ''}`}
                  onClick={() => p.id && handleSwitchProfile(p.id)}
                >
                  <span className="profile-tab-name">{p.name || 'Untitled'}</span>
                  {p.position && <span className="profile-tab-role">{p.position}</span>}
                  {p.is_active && <span className="profile-tab-badge">{t('profileActive')}</span>}
                </button>
              ))}
            </div>
          )}

          {/* Profile Cards (View Mode) */}
          {!editMode && activeProfile && (
            <div className="profile-cards">
              {/* Position & Company Card */}
              <div className="profile-info-card">
                <div className="profile-info-card-header">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <h4>{t('profileTargetRole')}</h4>
                </div>
                <div className="profile-info-card-body">
                  {activeProfile.position ? (
                    <p className="profile-info-value">{activeProfile.position}</p>
                  ) : (
                    <p className="profile-info-empty">{t('profileNotSet')}</p>
                  )}
                  {activeProfile.company_name && (
                    <p className="profile-info-company">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <path d="M3 21h18M3 7v14M21 7v14M6 11h4M6 15h4M14 11h4M14 15h4M10 21V17h4v4" />
                        <path d="M3 7l9-4 9 4" />
                      </svg>
                      {activeProfile.company_name}
                    </p>
                  )}
                </div>
              </div>

              {/* CV Summary Card */}
              <div className="profile-info-card">
                <div className="profile-info-card-header">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10,9 9,9 8,9" />
                  </svg>
                  <h4>{t('profileCvSummaryTitle')}</h4>
                </div>
                <div className="profile-info-card-body">
                  {activeProfile.cv_summary ? (
                    <div className="profile-info-summary">
                      <Markdown>{activeProfile.cv_summary}</Markdown>
                    </div>
                  ) : activeProfile.cv_text ? (
                    <p className="profile-info-pending">{t('profileSummaryPending')}</p>
                  ) : (
                    <p className="profile-info-empty">{t('profileCvNotUploaded')}</p>
                  )}
                </div>
              </div>

              {/* JD Summary Card */}
              <div className="profile-info-card">
                <div className="profile-info-card-header">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                  </svg>
                  <h4>{t('profileJdSummaryTitle')}</h4>
                </div>
                <div className="profile-info-card-body">
                  {activeProfile.jd_summary ? (
                    <div className="profile-info-summary">
                      <Markdown>{activeProfile.jd_summary}</Markdown>
                    </div>
                  ) : activeProfile.job_description ? (
                    <p className="profile-info-pending">{t('profileSummaryPending')}</p>
                  ) : (
                    <p className="profile-info-empty">{t('profileJdNotUploaded')}</p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="profile-card-actions">
                <button className="btn btn-primary" onClick={startEditing}>
                  {t('profileEdit')}
                </button>
                {activeProfile.id && (
                  <button
                    className="btn btn-danger-outline"
                    onClick={() => activeProfile.id && handleDeleteProfile(activeProfile.id)}
                  >
                    {t('profileDelete')}
                  </button>
                )}
                {profileStatus === 'saved' && (
                  <span className="profile-status profile-status-success">{t('profileSaved')}</span>
                )}
                {profileStatus === 'uploaded' && (
                  <span className="profile-status profile-status-success">{t('profileFileUploaded')}</span>
                )}
              </div>
            </div>
          )}

          {/* No profiles yet */}
          {!editMode && profiles.length === 0 && (
            <div className="profile-empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
              <p>{t('profileEmptyState')}</p>
              <button className="btn btn-primary" onClick={startCreating}>
                {t('profileCreateFirst')}
              </button>
            </div>
          )}

          {/* Edit / Create Form */}
          {editMode && (
            <div className={`profile-edit-form ${profileSaving ? 'profile-edit-form-disabled' : ''}`}>
              {profileSaving && (
                <div className="profile-saving-overlay">
                  <div className="profile-saving-spinner" />
                  <span>{t('profileSaving')}</span>
                </div>
              )}

              <div className="profile-field">
                <label htmlFor="profile-name">{t('profileName')}</label>
                <input
                  id="profile-name"
                  type="text"
                  value={profileForm.name}
                  onChange={(e) => handleProfileChange('name', e.target.value)}
                  placeholder={t('profileNamePlaceholder')}
                  disabled={profileSaving}
                />
              </div>

              <div className="profile-row">
                <div className="profile-field">
                  <label htmlFor="profile-position">{t('profilePosition')}</label>
                  <input
                    id="profile-position"
                    type="text"
                    value={profileForm.position}
                    onChange={(e) => handleProfileChange('position', e.target.value)}
                    placeholder={t('profilePositionPlaceholder')}
                    disabled={profileSaving}
                  />
                </div>
                <div className="profile-field">
                  <label htmlFor="profile-company">{t('profileCompany')}</label>
                  <input
                    id="profile-company"
                    type="text"
                    value={profileForm.company_name}
                    onChange={(e) => handleProfileChange('company_name', e.target.value)}
                    placeholder={t('profileCompanyPlaceholder')}
                    disabled={profileSaving}
                  />
                </div>
              </div>

              {/* Job Description with File Upload */}
              <div className="profile-field">
                <div className="profile-field-header">
                  <label htmlFor="profile-jd">{t('profileJobDescription')}</label>
                  <button
                    className="btn-upload"
                    onClick={() => jdFileRef.current?.click()}
                    disabled={uploading === 'jd' || profileSaving}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17,8 12,3 7,8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {uploading === 'jd' ? t('profileUploading') : t('profileUploadFile')}
                  </button>
                  <input
                    ref={jdFileRef}
                    type="file"
                    accept=".pdf,.docx,.doc,.txt"
                    style={{ display: 'none' }}
                    disabled={profileSaving}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileUpload('jd', file)
                      e.target.value = ''
                    }}
                  />
                </div>
                <textarea
                  id="profile-jd"
                  rows={5}
                  value={profileForm.job_description}
                  onChange={(e) => handleProfileChange('job_description', e.target.value)}
                  placeholder={t('profileJobDescriptionPlaceholder')}
                  disabled={profileSaving}
                />
                <span className="profile-field-hint">{t('profileFileHint')}</span>
              </div>

              {/* CV with File Upload */}
              <div className="profile-field">
                <div className="profile-field-header">
                  <label htmlFor="profile-cv">{t('profileCvText')}</label>
                  <button
                    className="btn-upload"
                    onClick={() => cvFileRef.current?.click()}
                    disabled={uploading === 'cv' || profileSaving}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17,8 12,3 7,8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {uploading === 'cv' ? t('profileUploading') : t('profileUploadFile')}
                  </button>
                  <input
                    ref={cvFileRef}
                    type="file"
                    accept=".pdf,.docx,.doc,.txt"
                    style={{ display: 'none' }}
                    disabled={profileSaving}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileUpload('cv', file)
                      e.target.value = ''
                    }}
                  />
                </div>
                <textarea
                  id="profile-cv"
                  rows={6}
                  value={profileForm.cv_text}
                  onChange={(e) => handleProfileChange('cv_text', e.target.value)}
                  placeholder={t('profileCvTextPlaceholder')}
                  disabled={profileSaving}
                />
                <span className="profile-field-hint">{t('profileFileHint')}</span>
              </div>

              {/* Form Actions */}
              <div className="profile-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleProfileSave}
                  disabled={profileSaving}
                >
                  {profileSaving ? t('profileSaving') : t('profileSave')}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={cancelEditing}
                  disabled={profileSaving}
                >
                  {t('profileCancel')}
                </button>
                {profileStatus === 'saved' && (
                  <span className="profile-status profile-status-success">{t('profileSaved')}</span>
                )}
                {profileStatus === 'uploaded' && (
                  <span className="profile-status profile-status-success">{t('profileFileUploaded')}</span>
                )}
                {profileStatus === 'error' && (
                  <span className="profile-status profile-status-error">{t('profileSaveError')}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
